import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Footerr from "../components/Footerr/Footerr";
import { Header } from "../components/shared";
import servicesData from "../../data/servicesData.json";
import ServiceFAQs from "../components/ServiceFAQs/ServiceFAQs";
import usePageMeta from "../../seo/usePageMeta";

function ServicesPage() {
  usePageMeta({
    title: "Restaurant Consulting Services | Terces Solutions",
    description:
      "End-to-end restaurant consulting: process optimization, menu engineering, kitchen operations, inventory, marketing, delivery platforms, CRM, and financial management.",
    path: "/Services",
  });

  // Categorize services
  const categories = {
    "operations": {
      title: "Operational Excellence",
      description: "Streamline workflows and maximize efficiency",
      services: [
        "process-optimization",
        "menu-engineering",
        "kitchen-operations",
        "inventory-management"
      ]
    },
    "growth": {
      title: "Growth & Visibility",
      description: "Expand your reach and build your brand",
      services: [
        "branding-marketing",
        "digital-profiling",
        "delivery-platforms",
        "catering-events",
        "crm"
      ]
    },
    "management": {
      title: "Management & Sustainability",
      description: "Build a strong foundation for long-term success",
      services: [
        "financial-accounts",
        "employee-management",
        "infrastructure-management"
      ]
    }
  };

  // Icon mapping for each service
  const serviceIcons = {
    "process-optimization": "⚡",
    "financial-accounts": "💰",
    "employee-management": "👥",
    "infrastructure-management": "🏗️",
    "menu-engineering": "📋",
    "inventory-management": "📦",
    "kitchen-operations": "👨‍🍳",
    "delivery-platforms": "🚚",
    "catering-events": "🎉",
    "branding-marketing": "📢",
    "digital-profiling": "💻",
    "crm": "🤝"
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const categoryVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.4
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>

      {/* Hero Section - Enhanced Premium Cream Theme */}
      <section className="relative py-24 md:py-40 px-6 overflow-hidden bg-[#F6F4EF]">
        {/* Animated Decorative Elements */}
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" 
        />
        <motion.div 
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" 
        />
        
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(#1b7840 1px, transparent 1px), linear-gradient(90deg, #1b7840 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-block mb-8"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/60 backdrop-blur-sm rounded-full border border-primary/30 shadow-lg">
                <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                <span className="text-sm font-bold text-primary uppercase tracking-widest">
                  Our Expertise
                </span>
              </div>
            </motion.div>

            {/* Main Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight"
            >
              From Concept to Profit, <br/><span className="text-primary relative">
                We Build What Works
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 1, delay: 0.8 }}
                  className="absolute bottom-0 left-0 h-1 bg-primary/20 rounded-full"
                />
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base md:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-10"
            >
              Comprehensive Solutions and Services across operations, growth, and management—designed to drive sustainable success for your restaurant business
            </motion.p>

            {/* Stats with Better Design */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="grid grid-cols-3 gap-3 md:gap-4 w-full max-w-sm mx-auto md:max-w-lg"
            >
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl px-3 py-5 md:px-8 md:py-6 border border-gray-200 shadow-lg text-center">
                <div className="text-2xl md:text-4xl font-bold text-primary mb-1">12+</div>
                <div className="text-[10px] md:text-xs text-gray-600 uppercase tracking-widest">Services</div>
              </div>
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl px-3 py-5 md:px-8 md:py-6 border border-gray-200 shadow-lg text-center">
                <div className="text-2xl md:text-4xl font-bold text-primary mb-1">3</div>
                <div className="text-[10px] md:text-xs text-gray-600 uppercase tracking-widest">Categories</div>
              </div>
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl px-3 py-5 md:px-8 md:py-6 border border-gray-200 shadow-lg text-center">
                <div className="text-2xl md:text-4xl font-bold text-primary mb-1">100%</div>
                <div className="text-[10px] md:text-xs text-gray-600 uppercase tracking-widest">Customized</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Categories Section */}
      <section className="py-20 px-6 bg-white">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-7xl mx-auto space-y-20"
        >
          {Object.entries(categories).map(([key, category]) => (
            <motion.div
              key={key}
              variants={categoryVariants}
              className="relative"
            >
              {/* Category Header with Side Accent */}
              <div className="mb-10 relative">
                <div className="flex items-center gap-6">
                  <div className="w-1 h-20 bg-primary rounded-full max-md:hidden" />
                  <div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                      {category.title}
                    </h2>
                    <p className="text-xl text-gray-600 max-md:text-sm">
                      {category.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Services Grid - Horizontal Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-10 max-md:pl-0 max-md:gap-4">
                {category.services.map((serviceId) => {
                  const service = servicesData[serviceId];
                  if (!service) return null;

                  return (
                    <motion.div
                      key={serviceId}
                      variants={cardVariants}
                      whileHover={{ x: 8, transition: { duration: 0.3 } }}
                      className="group"
                    >
                      <Link
                        to={`/services/${serviceId}`}
                        className="block h-full"
                      >
                        <div className="h-full bg-white rounded-xl md:rounded-none border border-gray-100 md:border-0 md:border-l-4 md:border-gray-200 md:hover:border-primary px-5 py-5 md:pl-6 md:pr-6 md:py-6 shadow-sm md:shadow-none transition-all duration-300 hover:shadow-lg">
                          <div className="flex items-start gap-4">
                            {/* Icon */}
                            <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300">
                              {serviceIcons[serviceId] || "🍽️"}
                            </div>

                            {/* Content */}
                            <div className="flex-1">
                              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors duration-300">
                                {service.title}
                              </h3>
                              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                                {service.tagline}
                              </p>

                              {/* Arrow */}
                              <div className="flex items-center text-primary font-semibold text-sm group-hover:translate-x-2 transition-transform duration-300">
                                <span>Explore service</span>
                                <svg
                                  className="w-4 h-4 ml-2"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Why Choose Our Services Section - Enhanced */}
      <section className="relative py-24 px-6 bg-[#F6F4EF] overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 bg-white/60 backdrop-blur-sm rounded-full mb-6 relative border border-primary/20">
              <span className="text-sm font-bold text-primary uppercase tracking-widest">
                Why Choose Us
              </span>
             
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Built on Experience, Driven by Results
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Partner with a team that understands the unique challenges of the restaurant industry
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-primary hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 text-3xl group-hover:scale-110 transition-transform duration-300">
                🎯
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Tailored Solutions
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Every restaurant is unique. Our services are customized to match your specific needs, challenges, and goals.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-primary hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 text-3xl group-hover:scale-110 transition-transform duration-300">
                💡
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Industry Expertise
              </h3>
              <p className="text-gray-600 leading-relaxed">
                4+ years of hands-on experience in restaurant operations, ensuring practical and proven strategies.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-primary hover:shadow-2xl transition-all duration-300 group"
            >
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 text-3xl group-hover:scale-110 transition-transform duration-300">
                📊
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Measurable Impact
              </h3>
              <p className="text-gray-600 leading-relaxed">
                We focus on delivering tangible results—improved efficiency, increased revenue, and sustainable growth.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
  

      {/* CTA Section - Enhanced */}
      <section className="relative py-28 px-6 overflow-hidden bg-gradient-to-br from-primary via-[#1a6d3d] to-[#155a32]">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              x: [0, 50, 0],
              y: [0, 30, 0]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-10 left-10 w-96 h-96 bg-white rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              x: [0, -50, 0],
              y: [0, -30, 0]
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"
          />
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Decorative Element */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-2 px-6 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <span className="text-sm font-bold text-black uppercase tracking-widest">
                  Let's Get Started
                </span>
              </div>
            </div>

            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-black mb-6 leading-tight">
              Ready to Transform Your Restaurant?
            </h2>
            <p className="text-xl md:text-2xl text-black/90 mb-12 leading-relaxed max-w-2xl mx-auto">
              Let's discuss how our services can help you achieve operational excellence and sustainable growth
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/Contact"
                className="inline-flex items-center gap-3 px-10 py-4 bg-white text-primary rounded-full font-bold text-lg hover:bg-cream hover:scale-105 transition-all duration-300 shadow-2xl"
              >
                <span>Get Started Today</span>
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </Link>


            </div>
          </motion.div>
        </div>
      </section>
      </main>

      <Footerr />
    </div>
  );
}

export default ServicesPage;